import 'popper.js';
import tippy from 'tippy.js';
import { LitElement, html } from 'lit-element';

import { isValidTrackingObject } from '../tracking/utils';

import './icon';

const API_URL = '/api/brokers/instrument';

const BUTTON_CLASSES = {
	primary: 'button button--primary',
	secondary: 'button',
	small: 'button button--ghost button--icon-only button--small',
};

const setGtmEventsAction = (gtmPushData, brokerName) => {
	const { events = {} } = gtmPushData;

	events.action = brokerName;

	return gtmPushData;
};

const getGtmPushData = (gtmPushData, brokerName) => {
	gtmPushData = setGtmEventsAction(gtmPushData, brokerName);

	return JSON.stringify(gtmPushData);
};

/* Unfortunately the attribute names cannot be variable */
const renderBrokerLink = (broker, shouldTrack, gtmPushData) => html`
	<li class="dropdown__item dropdown__item--padded text-center">
		<a href="${broker.purchaseUrl}" target="_blank" role="menuitem"
			?data-gtm-click="${shouldTrack}"
			data-gtm-push="${getGtmPushData(gtmPushData, broker.name)}">

			<img src="${broker.imageUrl}" alt="${broker.name} logo" class="image image--small" />
		</a>
	</li>
`;

const renderDropdown = (id, brokers, gtmPushData) => {
	const shouldTrack = isValidTrackingObject(gtmPushData);

	return html`
		<ul id="broker-dropdown-${id}" class="dropdown__list" role="menu" aria-labelledby="transaction-${id}">
			${brokers.map(broker => renderBrokerLink(broker, shouldTrack, gtmPushData))}
		</ul>
	`;
};


class BrokerDropdown extends LitElement {
	static get properties() {
		return {
			id: { type: String },
			isin: { type: String },
			buttonLabel: { type: String },

			type: { type: String },
			gtmPushData: { type: Object },
			isLoading: { type: Boolean },
			hasLoaded: { type: Boolean },
			brokers: { type: Array },
		};
	}

	constructor() {
		super();

		this.isLoading = false;
		this.hasLoaded = false;
		this.brokers = [];

		this.gtmPushData = {};
		this.type = 'small';
		this.buttonLabel = 'Transaction';
	}

	disconnectedCallback() {
		if (this.dropdown) {
			this.dropdown.destroy();
		}

		super.disconnectedCallback();
	}

	render() {
		const buttonClasses = BUTTON_CLASSES[this.type];
		const label = this.type === 'primary' || this.type === 'secondary'
			? this.buttonLabel
			: '';

		return html`
			<button class="${buttonClasses}"
				@click="${this.createDropdown}"
				id="transaction-${this.id}"
				aria-controls="broker-dropdown-${this.id}"
				aria-owns="broker-dropdown-${this.id}"
				aria-haspopup="true">
				<x-loader .isLoading="${this.isLoading}"></x-loader>
				<x-icon icon="cart"></x-icon>

				${label}
			</button>

			${this.hasLoaded ? renderDropdown(this.id, this.brokers, this.gtmPushData) : ''}
		`;
	}

	async createDropdown() {
		if (this.hasLoaded) {
			return;
		}

		this.isLoading = true;

		this.brokers = await fetch(`${API_URL}/${this.isin}`)
			.then(res => res.json());

		this.isLoading = false;
		this.hasLoaded = true;

		await this.updateComplete;

		const button = this.querySelector('button');
		const menu = this.querySelector('ul');
		const placement = this.type === 'primary'
			? 'bottom'
			: 'right';

		this.dropdown = tippy(button, {
			placement,

			boundary: 'viewport',
			content: menu,
			interactive: true,
			trigger: 'click',

			interactiveBorder: 10,
			interactiveDebounce: 0,

			delay: [0, 0],
			duration: [0, 0],
		});

		this.dropdown.show();
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-broker-dropdown', BrokerDropdown);

export default BrokerDropdown;
